import {
   CurrentPractitionerEwusConfigurationSetCommand,
   EwusAccountBlockedFault,
   EwusAccountLoginFault,
   EwusPasswordExpiredFault,
} from '@dreryk/egabinet-ewus-frontend-api';
import {
   EzwmAccountBlockedFault,
   EzwmAccountLoginFault,
   EzwmPasswordExpiredFault,
   EzwmPractitionerConfigurationUpdateCommand,
} from '@dreryk/egabinet-ezwm-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice, Slot } from '@dreryk/watermelon';

import { nfzLoginTranslationKeys } from '../../../translations';

import { NfzLoginFormBean } from './NfzLoginForm.types';
import { PasswordChangeEnabledFormBeanPart } from './fields/PasswordChangeEnabledField';
import { ezwmMfaStrategyVisitor, mfaStrategyVisitor } from './NfzLoginForm.utils';
import { EwusEnabledFormBeanPart } from './fields/EwusEnabledField';
import { EzwmEnabledFormBeanPart } from './fields/EzwmEnabledField';

type CurrentPractitionerEwusConfigurationSetCommandOptions = {
   onSuccess: VoidFunction;
};

export const useCurrentPractitionerEwusConfigurationSetCommand = (
   options: CurrentPractitionerEwusConfigurationSetCommandOptions
): UseCqrsCommandResult<
   void,
   NfzLoginFormBean<PasswordChangeEnabledFormBeanPart<false> & EwusEnabledFormBeanPart<true>>,
   EwusAccountLoginFault | EwusPasswordExpiredFault | EwusAccountBlockedFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new CurrentPractitionerEwusConfigurationSetCommand();
         command.login = parameters.login!;
         command.password = parameters.password!;

         mfaStrategyVisitor(parameters)({
            visitSecretCode: ({ mfaSecret }) => {
               command.mfaSecret = Choice.withUseDefaultIfNull(mfaSecret);
            },
            visitTotp: ({ mfaAuthorizationCode }) => {
               command.mfaTotp = Choice.of(mfaAuthorizationCode);
            },
         });

         command.personnelCode = Choice.withUseDefaultIfNull(parameters.personnelCode);
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(nfzLoginTranslationKeys.loginCredentialsForNfzHaveBeenSaved);
            options.onSuccess();
         },
         onFault: {
            'egabinet-ewus-frontend-api:EwusAccountLoginFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.connectionWithNfzFailedLoginInformationIncorrect),
            'egabinet-ewus-frontend-api:EwusPasswordExpiredFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.nfzPasswordHasExpiredYouNeedToMakePasswordChange),
            'egabinet-ewus-frontend-api:EwusAccountBlockedFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.nfzAccountBlocked),
         },
      }
   );
};

type EzwmLoginCommandOptions = {
   onSuccess: VoidFunction;
};

export const useEzwmLoginCommand = (
   options: EzwmLoginCommandOptions
): UseCqrsCommandResult<
   void,
   NfzLoginFormBean<PasswordChangeEnabledFormBeanPart<false> & EzwmEnabledFormBeanPart<true>>,
   EzwmAccountLoginFault | EzwmPasswordExpiredFault | EzwmAccountBlockedFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new EzwmPractitionerConfigurationUpdateCommand();
         command.login = parameters.login!;
         command.newPassword = parameters.password!;
         command.oldPassword = Slot.empty();
         command.personnelCode = Choice.withUseDefaultIfNull(parameters.personnelCode ?? null);

         ezwmMfaStrategyVisitor(parameters)({
            visitSecretCode: ({ mfaSecret }) => {
               command.mfaSecret = Choice.withUseDefaultIfNull(mfaSecret);
            },
            visitTotp: ({ mfaAuthorizationCode }) => {
               command.mfaTotp = Choice.withUseDefaultIfNull(mfaAuthorizationCode);
            },
         });

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(nfzLoginTranslationKeys.loginCredentialsForNfzHaveBeenSaved);
            options.onSuccess();
         },
         onFault: {
            'egabinet-ezwm-frontend-api:EzwmAccountLoginFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.connectionWithNfzFailedLoginInformationIncorrect),
            'egabinet-ezwm-frontend-api:EzwmPasswordExpiredFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.nfzPasswordHasExpiredYouNeedToMakePasswordChange),
            'egabinet-ezwm-frontend-api:EzwmAccountBlockedFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.nfzAccountBlocked),
         },
      }
   );
};

export const useEzwmChangePasswordCommand = (
   options: EzwmLoginCommandOptions
): UseCqrsCommandResult<
   void,
   NfzLoginFormBean<PasswordChangeEnabledFormBeanPart<true> & EzwmEnabledFormBeanPart<true>>,
   EzwmAccountLoginFault | EzwmPasswordExpiredFault | EzwmAccountBlockedFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new EzwmPractitionerConfigurationUpdateCommand();
         command.login = parameters.login!;
         command.oldPassword = Slot.of(parameters.password);
         command.newPassword = parameters.newPassword!;
         command.personnelCode = Choice.withUseDefaultIfNull(parameters.personnelCode ?? null);

         ezwmMfaStrategyVisitor(parameters)({
            visitSecretCode: ({ mfaSecret }) => {
               command.mfaSecret = Choice.withUseDefaultIfNull(mfaSecret);
            },
            visitTotp: ({ mfaAuthorizationCode }) => {
               command.mfaTotp = Choice.withUseDefaultIfNull(mfaAuthorizationCode);
            },
         });

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(nfzLoginTranslationKeys.loginCredentialsForNfzHaveBeenSaved);
            options.onSuccess();
         },
         onFault: {
            'egabinet-ezwm-frontend-api:EzwmAccountLoginFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.connectionWithNfzFailedLoginInformationIncorrect),
            'egabinet-ezwm-frontend-api:EzwmPasswordExpiredFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.nfzPasswordHasExpiredYouNeedToMakePasswordChange),
            'egabinet-ezwm-frontend-api:EzwmAccountBlockedFault': () =>
               dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.nfzAccountBlocked),
         },
      }
   );
};
